import {
  Box,
  Badge,
  Card,
  Chip,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import { History, UploadFile } from "@mui/icons-material";

import { PrimaryButton } from "@/components/common";

import {
  PatientSupportingDocumentRecord,
  PatientSupportingDocumentTypeConfig,
} from "./types";
import { PatientSupportingDocumentList } from "./PatientSupportingDocumentList";

interface PatientSupportingDocumentCardProps {
  config: PatientSupportingDocumentTypeConfig;
  documents: PatientSupportingDocumentRecord[];
  /** Quantidade de removidos (histórico) deste tipo. */
  cancelledCount?: number;
  onAdd: () => void;
  onReplace: (document: PatientSupportingDocumentRecord) => void;
  onRemove: (document: PatientSupportingDocumentRecord) => void;
  /** Abre o documento selecionado no CommonDialog. */
  onView: (document: PatientSupportingDocumentRecord) => void;
  /** Abre o histórico de documentos removidos deste tipo. */
  onOpenHistory: () => void;
  compact?: boolean;
  /** layout da tela dedicada — cards mais baixos no web */
  pageLayout?: boolean;
}

export function PatientSupportingDocumentCard({
  config,
  documents,
  cancelledCount = 0,
  onAdd,
  onReplace,
  onRemove,
  onView,
  onOpenHistory,
  compact = false,
  pageLayout = false,
}: PatientSupportingDocumentCardProps) {
  const theme = useTheme();
  const { Icon, title, description, accentKey } = config;
  const accent = theme.palette[accentKey].main;
  const isUploaded = documents.length > 0;
  const isDense = compact || pageLayout;
  // Altura alinhada ao padrão Primary da plataforma.
  const buttonHeight = pageLayout ? "36px" : "40px";

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 2,
        border: "1px solid",
        borderColor: isUploaded
          ? alpha(accent, 0.35)
          : alpha(theme.palette.divider, 0.9),
        bgcolor: "background.paper",
        overflow: "hidden",
        // Altura uniforme no grid (com ou sem observações).
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          height: 3,
          bgcolor: accent,
          opacity: isUploaded ? 1 : 0.35,
        }}
      />

      <Stack spacing={isDense ? 1 : 1.5} sx={{ p: isDense ? 1.25 : 2, flex: 1 }}>
        <Stack direction="row" spacing={1.25} alignItems="flex-start">
          <Box
            sx={{
              width: isDense ? 36 : 44,
              height: isDense ? 36 : 44,
              borderRadius: 1.5,
              bgcolor: alpha(accent, 0.12),
              color: accent,
              display: "grid",
              placeItems: "center",
              flexShrink: 0,
            }}
          >
            <Icon fontSize="small" />
          </Box>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              spacing={1}
            >
              <Typography
                variant="subtitle2"
                fontWeight={700}
                lineHeight={1.3}
                sx={{ minWidth: 0 }}
              >
                {title}
              </Typography>
              <Stack
                direction="row"
                alignItems="center"
                spacing={0.5}
                flexShrink={0}
              >
                <Chip
                  size="small"
                  label={
                    isUploaded
                      ? `${documents.length} enviado${
                          documents.length === 1 ? "" : "s"
                        }`
                      : "Pendente"
                  }
                  sx={{
                    height: 22,
                    fontWeight: 700,
                    bgcolor: isUploaded
                      ? alpha(theme.palette.success700.main, 0.12)
                      : alpha(theme.palette.warning500.main, 0.14),
                    color: isUploaded ? "success700.main" : "warning800.main",
                  }}
                />
                <Tooltip
                  title={
                    "Histórico de documentos"
                  }
                >
                  <IconButton
                    aria-label={`Histórico de ${title}`}
                    onClick={onOpenHistory}
                    sx={{ color: accent }}
                  >
                    <Badge
                      badgeContent={cancelledCount > 0 ? cancelledCount : 0}
                      color="primary"
                      invisible={cancelledCount === 0}
                    >
                      <History />
                    </Badge>
                  </IconButton>
                </Tooltip>
              </Stack>
            </Stack>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mt: 0.5, lineHeight: 1.45 }}
            >
              {description}
            </Typography>
          </Box>
        </Stack>

        <PatientSupportingDocumentList
          documents={documents}
          accent={accent}
          pageLayout={pageLayout}
          onView={onView}
          onReplace={onReplace}
          onRemove={onRemove}
        />

        <Stack spacing={0.75} sx={{ mt: "auto", width: "100%" }}>
          {/* Adicionar sempre cria um novo registro e preserva os anteriores. */}
          <PrimaryButton
            width="100%"
            height={buttonHeight}
            onClick={onAdd}
            startIcon={<UploadFile />}
          >
            Adicionar documento
          </PrimaryButton>
        </Stack>
      </Stack>
    </Card>
  );
}
